import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlarmClock } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { useAuth } from '../context/AuthContext';
import { useProUpsell } from '../context/ProUpsellContext';
import { followAuction, unfollowAuction, reminderKey, getReminderState } from '../lib/auctionReminders';
import { REMINDER_LABELS, useReminderState, AuctionReminderChannels } from './AuctionReminderChannels';

const isOpenAuction = (l) => (l.is_auction || l.sale_type === 'Auktion') && !l.sold
  && (!l.end_at || new Date(l.end_at) > new Date());

// ⏰ Segui un'asta: avviso Telegram/email ~1 ora prima della fine (Pro).
export const AuctionReminderButton = ({ listing, variant = 'icon', onLoginRequest = null, idSuffix = '' }) => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'it').split('-')[0];
  const L = REMINDER_LABELS[lang] || REMINDER_LABELS.it;
  const { user } = useAuth() || {};
  const { showProUpsell } = useProUpsell();
  const { keys, chosen, linked } = useReminderState();
  const [busy, setBusy] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);
  if (!isOpenAuction(listing)) return null;
  const on = keys.has(reminderKey(listing));

  const follow = async () => {
    try {
      await followAuction(listing);
      toast.success(L.followed);
    } catch (err) {
      if (err?.code === 'PRO_REQUIRED') showProUpsell('alert');
      else toast.error(err?.status === 409 ? L.ended : err.message);
    }
  };

  const handleClick = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) {
      if (onLoginRequest) onLoginRequest(); else toast.error(L.login);
      return;
    }
    if (!user.is_pro) { showProUpsell('alert'); return; }
    if (busy) return;
    setBusy(true);
    try {
      if (on) { await unfollowAuction(listing); toast.success(L.unfollowed); }
      else if (!chosen) setPickOpen(true);
      else await follow();
    } catch { toast.error(L.proOnly); }
    finally { setBusy(false); }
  };

  const label = on ? L.following : L.follow;
  const testid = `auction-reminder-button${variant === 'full' ? '-full' : ''}${idSuffix}`;
  return (
    <>
      {variant === 'full' ? (
        <button type="button" data-testid={testid} onClick={handleClick} disabled={busy}
          aria-pressed={on} title={on ? L.unfollow : L.follow}
          className={`flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-95 border ${
            on ? 'bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/30' : 'bg-muted text-foreground hover:bg-muted/80 border-border/60'}`}>
          <AlarmClock size={15} /> <span className="hidden sm:inline">{label}</span>
        </button>
      ) : (
        <button type="button" data-testid={testid} onClick={handleClick} disabled={busy}
          aria-pressed={on} aria-label={label} title={on ? L.unfollow : L.follow}
          className={`h-8 w-8 inline-flex items-center justify-center rounded-md transition-colors hover:bg-muted ${
            on ? 'text-sky-500' : 'text-muted-foreground hover:text-sky-500'}`}>
          <AlarmClock size={16} strokeWidth={on ? 2.6 : 2} />
        </button>
      )}
      <Dialog open={pickOpen} onOpenChange={setPickOpen}>
        <DialogContent className="max-w-md" onClick={e => e.stopPropagation()} data-testid="auction-reminder-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <AlarmClock size={18} className="text-sky-500" /> {L.pickTitle}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{L.settingsDesc}</p>
          <AuctionReminderChannels lang={lang} onSaved={(v) => {
            if (!getReminderState().keys.has(reminderKey(listing))) follow();
            if (!v.includes('telegram') || linked) setPickOpen(false);
          }} />
        </DialogContent>
      </Dialog>
    </>
  );
};
